import { CompactOrgResponse, OrgAddressResponse, OrgId } from '@business/entities/organization';
import { InvoiceDiscountData } from '@modules/common/InvoiceDiscount';
import { TransactionInvoiceResponse } from '../TransactionInvoice';
import { InvoiceSalesTaxData } from './../../../modules/common/InvoiceSalesTax/InvoiceSalesTax';
import { POAddressFormData } from './../../../modules/orders/EditPOPage/POForm';
import { CompactUserResponse, UserId } from './../account/UserResponse';
import { BaseResponse } from './../common/BaseResponse';
import { CompactContactResponse } from './../contact/ContactResponse';
import { SimpleOrgResponse } from './../organization/OrgResponse';
import { InvoiceSettingsResponse } from './../TransactionInvoice/TransactionInvoiceResponse';
import { OrderShipmentResponse } from './OrderShipmentResponse';
import { OrderStageId, OrderStageResponse } from './OrderStageResponse';
import { PurchaseOrderResponse, PurchaseOrderLineItem, PoType } from './PurchaseOrderResponse';
import { OrderTermResponse } from './OrderTermResponse';
import { OrderVendorResponse } from './OrderVendorResponse';
import { OrderProductResponse } from './OrderProductResponse';
import { LabourTypeResponse } from '../labour-type';
import { TMBoardId } from '../tm/TimeMaterialBoardResponse';
import { OrderEstimateStatus as TMOrderEstimateStatus } from '../tm';

export type OrderId = string;
export enum OrderStatus {
  open = 'open',
  completed = 'completed',
  canceled = 'canceled',
  draft = 'draft',
}
export enum OrderShippingOption {
  ready = 'ready',
  complete = 'complete',
}

export interface CompactOrderResponse extends BaseResponse {
  id: OrderId;
  name: string;
  description: string;
  organization: CompactOrgResponse | SimpleOrgResponse;
  status: OrderStatus;
  stageId: OrderStageId;
  stage: OrderStageResponse;
  term: OrderTermResponse;
  orderNumber: string;
  total: number;
  balance: number;
  deliveryDate?: number;
  shippedDate?: number;
  billingEmail: string;
  shippingMethod: string;
  shippingAccountNo: string;
  trackId: string;
  billingAddress: OrgAddressResponse;
  shippingAddress: OrgAddressResponse;
  contact: CompactContactResponse;
  salesReps: UserId[];
  salesRepsNames: string[];
  shippingOption: OrderShippingOption;
  customerPoNo: string;
  purchaseOrders: PurchaseOrderResponse[];
  canEdit: boolean;
  cc: string;
  bcc: string;
  isHide: boolean;
  pendingVendors: OrderVendorResponse[];
  boardId: TMBoardId;
  installDate: number;
}

export interface BoardOrderResponse extends CompactOrderResponse {
  warningAItems: PurchaseOrderLineItem[];
  warningSItems: PurchaseOrderLineItem[];
  shippedItems: PurchaseOrderLineItem[];
}

export interface OrderResponse extends CompactOrderResponse {
  estimate: OrderEstimateResponse;
  invoices: TransactionInvoiceResponse[];
  settings: InvoiceSettingsResponse;
  logs: OrderStageLogResponse[];
  shipments: OrderShipmentResponse[];
}

export interface OrderStageLogResponse extends BaseResponse {
  id: string;
  stageId: OrderStageId;
  orderId: OrderId;
  time: number;
  user: CompactUserResponse;
  contact: CompactContactResponse;
}

export enum EstimateApprovalAction {
  noCreateInvoice = 'noCreateInvoice',
  createSaveInvoice = 'createSaveInvoice',
  createSendInvoice = 'createSendInvoice',
}

export type OrderEstimateId = string;

export enum OrderEstimateStatus {
  accepted = 'accepted',
  rejected = 'rejected',
  pending = 'pending',
  draft = 'draft',
  sent = 'sent',
}
export interface OrderEstimateResponse extends BaseResponse {
  id: OrderEstimateId;
  name: string;
  number: string;
  status: TMOrderEstimateStatus;
  reason: string;
  importId: string;
  docNumber: string;
  totalAmount: number;
  subTotal: number;
  total: number;
  balance: number;
  memo: string;
  message: string;
  estimateDate: number;
  expirationDate: number;
  user: CompactUserResponse;
  items: EstimateLineItem[];
  discount: InvoiceDiscountData;
  salesTax: InvoiceSalesTaxData;
}

export interface EstimateLineItem {
  id: number;
  productId: string;
  serviceId: string;
  classId: string;
  productTitle: string;
  productSku: string;
  quantity: number;
  unitPrice: number;
  description: string;
  vendorId: string;
  tierId: number;
  thumbnail: string;
  unitOfMeasure: string;
  taxable: boolean;
  cost: number;
  sectionId: number | undefined;
}

export interface EstimateCopyLineItem extends EstimateLineItem {
  lineCopyId: string;
  product: OrderProductResponse;
}

export interface OrderShippingRequest {
  deliveryDate?: number;
  shippedDate?: number;
  shippingMethod: string;
  shippingAccountNo: string;
  trackId: string;
}

export interface OrderEmailTemplateRequest {
  to?: string;
  cc?: string;
  bcc?: string;
  body?: string;
  isSave?: boolean;
  files?: File[];
  poType?: PoType;
}

export interface OrderRequest extends OrderEmailTemplateRequest {
  orgId: OrgId;
  billingEmail: string;
  billingAddress?: string | POAddressFormData;
  memo: string;
  message: string;
  status: string;
  poNumber?: string;
  shippingAddress?: string | POAddressFormData;
}

export interface OrderCancelRequest {
  reason: string;
  deleteEstimate?: boolean;
  deleteInvoice?: boolean;
  deletePurchaseOrder?: boolean;
}

export interface OrderWithItemsResposne {
  id: OrderId;
  title: string;
  items: EstimateCopyLineItem[];
}

export interface ReceiveItemRequest {
  itemId: string;
  sku: string;
  quantity: number;
}

export interface LaborReportRecords {
  phaseName: string;
  soldHours: number;
  usedHours: number;
  soldHoursF: string;
  usedHoursF: string;
  laborId: string;
  notUsedHours: string;
  notUsedHoursF: string;
}
